import { type ReactNode } from "react";
import {
  DenseTable,
  estimateSuffix,
  defaultNumberFormat,
  type Column,
} from "../../components/index.ts";
import type { IdentityMcpDenialPage } from "./use-identity-mcp.ts";
import { quantityMeasure, reasonLabel } from "./vocab.ts";

/**
 * The denials-by-reason table (task 7.3; requirement 13.3). It accompanies the stacked chart with
 * the exact totals: one row per catalog `identity_mcp_reason`, zero-filled, so a reason that never
 * fired reads as a real zero rather than being absent (FR-4.3). Each total is a `Quantity` labelled
 * with its exact/estimate confidence (design 12.3). The busiest reasons sort to the top so the
 * dominant denial drivers are immediately legible; a zero-total reason sorts last but stays visible.
 */

type DenialRow = IdentityMcpDenialPage["items"][number];

export function DenialsTable(props: { items: readonly DenialRow[] }): ReactNode {
  // Busiest first by total; a null/zero total sorts last.
  const sorted = [...props.items].sort((a, b) => total(b) - total(a));

  const columns: readonly Column<DenialRow>[] = [
    {
      key: "reason",
      header: "Reason",
      isRowHeader: true,
      cell: (r) => (
        <span className="imcp-denials__key">
          <span className="imcp-denials__reason">{reasonLabel(r.reason)}</span>
          <code className="imcp-denials__code">{r.reason}</code>
        </span>
      ),
    },
    {
      key: "total",
      header: "Denials",
      align: "right",
      cell: (r) => {
        const q = r.total;
        const measure = quantityMeasure(q);
        const text = q.value === null ? "n/a" : `${defaultNumberFormat(q.value)}${estimateSuffix(measure)}`;
        return (
          <span className="imcp-num" data-measure={measure} title={`Denials for ${reasonLabel(r.reason)}`}>
            {text}
          </span>
        );
      },
    },
  ];

  return (
    <section className="imcp-denials" aria-label="Denials by reason">
      <h2 className="imcp-section__title">Denials by reason</h2>
      <DenseTable
        caption="Identity MCP denials by reason over the window, zero-filled over the catalog members"
        columns={columns}
        rows={sorted}
        rowKey={(r) => r.reason}
        stickyHeader
        emptyMessage="No Identity MCP denials were recorded in this window."
      />
    </section>
  );
}

/** The numeric total for sorting; a null total sorts to the bottom. */
function total(r: DenialRow): number {
  const v = r.total.value;
  return v === null ? -1 : v;
}
